import type { DataSourceStatus } from '../../providers/DataSourceManager.types';

/**
 * What a bound component should be showing: its data, or one of the three
 * states that stand in for data.
 *
 * Deliberately not `DataSourceStatus` renamed. A status describes a fetch; a
 * surface describes a render, and the two differ in both directions -- a
 * resolved fetch can still have nothing to show, and a component bound to a
 * source the manager has never heard of has no status at all yet must render
 * something.
 */
export type BoundStatusSurface = 'loading' | 'ready' | 'empty' | 'error';

/**
 * Why a component has nothing to show.
 *
 * Two questions were being answered by one word. The chart frame reads the
 * manager's `empty` status -- "the query returned no rows" -- while the data
 * repeater counts the rows left after its own extraction, sorting, and
 * paging -- "nothing survived my narrowing". Both are real, and today both
 * consumers happen to render the same copy for either, which is exactly how a
 * shared component ends up silently picking one and being wrong for the other.
 *
 * So the surface carries the reason instead of choosing. Consumers that do not
 * care can ignore it; a consumer that wants "no data for this term" to read
 * differently from "no data matching your filters" has the distinction
 * available rather than lost.
 */
export type BoundEmptyReason = 'source-empty' | 'narrowed';

/**
 * What failed, when a surface is `'error'`.
 *
 * `source` is a fetch that did not return usable rows; `adapter` is rows that
 * returned and could not be read into props. The remedies differ -- one is a
 * data question for whoever owns the query, the other a configuration defect
 * in the document -- so reporting them as one error sends readers to the
 * wrong place roughly half the time.
 */
export type BoundErrorSource = 'source' | 'adapter';

export interface BoundStatus {
  readonly surface: BoundStatusSurface;
  /** Set only when `surface` is `'empty'`. */
  readonly emptyReason?: BoundEmptyReason;
  /** Set only when `surface` is `'error'`. */
  readonly errorSource?: BoundErrorSource;
  /**
   * How old the data on screen is, in milliseconds, or `undefined` when the
   * source has never produced any.
   *
   * An age rather than a timestamp because that is what a consumer renders,
   * and computing it here means one clock read per component instead of each
   * consumer reaching for its own.
   */
  readonly ageMs?: number;
  /**
   * Whether that age exceeds the window the caller considers current. Absent
   * when the caller declares no window, because staleness is a judgment about
   * the data's use, not a property of the fetch.
   */
  readonly isStale?: boolean;
}

export interface BoundStatusInput {
  /**
   * The manager's status for the bound source, or `undefined` when the manager
   * does not know the source.
   */
  readonly status?: DataSourceStatus;
  /**
   * Whether a source was authored at all. A component bound to a source the
   * manager has not registered yet is loading, not ready -- while a component
   * bound to nothing is not waiting on anything.
   */
  readonly bound?: boolean;
  /**
   * Status to assume when nothing is bound, for consumers that receive their
   * data as a prop rather than through the manager. Defaults to `'resolved'`,
   * which renders children.
   */
  readonly unboundStatus?: DataSourceStatus;
  /**
   * Whether the consumer's own narrowing left nothing to render. Only consulted
   * once the source has resolved: rows cannot survive a query that failed, and
   * reporting "no matches" while a fetch is still in flight would flash an
   * empty state over data that is about to arrive.
   */
  readonly narrowedToNothing?: boolean;
  /**
   * Whether the component's render adapter failed to read the rows it was
   * given.
   *
   * Outranks every fetch status, because a fetch that succeeded is not the
   * reason there is nothing on screen. Checked before `narrowedToNothing` for
   * the same reason: an adapter that could not read the rows never narrowed
   * anything, so reporting "no matches" would describe a filter the reader
   * did not apply.
   *
   * Prefer {@link BoundStatusInput.adapterError}. That is the object the
   * binding enhancer already forwards; this boolean exists for a caller that
   * has already collapsed the failure to a flag.
   */
  readonly adapterFailed?: boolean;
  /**
   * Adapter failure forwarded by the binding enhancer (`adapterError`).
   *
   * A present object is the same verdict as `adapterFailed`: the fetch
   * succeeded and the rows could not be read. `null` and omission mean the
   * adapter did not fail.
   */
  readonly adapterError?: { readonly code: string } | null;
  /**
   * The instant the data on screen was current.
   *
   * Deliberately not "when the fetch returned", though the manager's
   * `sources[name].lastUpdatedAt` is one valid answer. For data served from a
   * pipeline, a fetch a second ago can return figures loaded last night, and
   * the age a reader cares about is the older one -- refetching does not make
   * yesterday's numbers current, so timing the fetch would report freshness the
   * data does not have. A caller that knows its own watermark passes that
   * instead, which is what the reporting dashboard does.
   */
  readonly lastUpdatedAt?: number;
  /**
   * How long data stays current, in milliseconds. Only the caller knows this:
   * a figure refreshed nightly is stale after a day, a live count after a
   * minute, and a static list never.
   */
  readonly staleAfterMs?: number;
  /** Injectable clock, so a caller can test an age without waiting for one. */
  readonly now?: number;
}

/**
 * Resolves what a data-bound component should render.
 *
 * Extracted because two components had grown the same decision independently:
 * `DataRepeater` branches on `!source || status === 'pending'`, then `error`,
 * then a row count, while the dashboard's chart frame maps status to a surface
 * and treats a bound-but-unknown source as pending. Same rules, different
 * spellings, and no single place to correct one.
 */
export function resolveBoundStatus({
  status,
  bound = false,
  unboundStatus = 'resolved',
  narrowedToNothing = false,
  adapterFailed = false,
  adapterError,
  lastUpdatedAt,
  staleAfterMs,
  now,
}: BoundStatusInput): BoundStatus {
  const effective: DataSourceStatus = bound
    ? // A bound source the manager has not registered is still arriving.
      (status ?? 'pending')
    : (status ?? unboundStatus);

  const age = freshness(lastUpdatedAt, staleAfterMs, now);

  // Ahead of the status switch, but only once the fetch itself succeeded.
  // An adapter failure describes rows that arrived and could not be read;
  // applying it over `error` / `empty` would mask the fetch verdict, and
  // applying it over `pending` would report a stale adapter result while
  // replacement rows are still in flight.
  if ((adapterFailed || adapterError != null) && effective === 'resolved') {
    return { surface: 'error', errorSource: 'adapter', ...age };
  }

  switch (effective) {
    case 'pending':
      return { surface: 'loading', ...age };
    case 'error':
      return { surface: 'error', errorSource: 'source', ...age };
    case 'empty':
      return { surface: 'empty', emptyReason: 'source-empty', ...age };
    case 'resolved':
      return narrowedToNothing
        ? { surface: 'empty', emptyReason: 'narrowed', ...age }
        : { surface: 'ready', ...age };
    default:
      // Statuses arrive from the manager at runtime, so the type union is a
      // claim about callers rather than a guarantee about values -- a switch
      // relying on exhaustiveness alone returns `undefined` here and crashes
      // whoever reads a field off it. `ready` is the conservative answer: a
      // status this function does not recognize is not evidence that the data
      // is missing, and blanking a component that has data is the worse error.
      return { surface: 'ready', ...age };
  }
}

/**
 * Age is reported on every surface, not just `ready`.
 *
 * A component refetching or showing an error still has whatever it drew last
 * on screen, and "this failed, and what you are looking at is an hour old" is
 * the sentence a reader needs. Suppressing age outside `ready` would report
 * the failure and hide the part that says whether to trust the numbers.
 */
function freshness(
  lastUpdatedAt: number | undefined,
  staleAfterMs: number | undefined,
  now: number | undefined,
): Pick<BoundStatus, 'ageMs' | 'isStale'> {
  if (lastUpdatedAt === undefined) {
    return {};
  }
  // Clamped at zero: a clock that moved backwards should read as "just now"
  // rather than as data from the future, which would also read as never stale.
  const ageMs = Math.max(0, (now ?? Date.now()) - lastUpdatedAt);
  if (staleAfterMs === undefined) {
    return { ageMs };
  }
  return { ageMs, isStale: ageMs > staleAfterMs };
}
